import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Plus, Check, Truck, Utensils, Star, Minus, ShoppingCart, Users, Clock } from 'lucide-react';
import { customerUnitPrice } from '../../utils/planningConstants';
import { CATEGORY_LABELS, SUB_CATEGORY_LABELS, PRICING_MODEL_LABELS } from '../../utils/planningConstants';
import { formatPrice } from '../../utils/planningHelpers';

export default function ServiceDetailModal({ hub }) {
  const {
    viewingService, setViewingService, selectedGalleryImage, setSelectedGalleryImage,
    detailTab, setDetailTab, selectedOptions, handleOptionChange,
    selectedServiceType, setSelectedServiceType, addToCart,
    customizingPkgServiceId, savePackageServiceOptions, eventDetails
  } = hub;

  // Local state for guest count / hours in the modal
  const [modalGuests, setModalGuests] = useState(50);
  const [modalHours, setModalHours] = useState(4);

  // Reset when service changes
  useEffect(() => {
    if (viewingService) {
      const isPerperson = viewingService.pricing_model === 'per_person' || viewingService.pricing_model === 'per_hour_per_person';
      const isPerHour = viewingService.pricing_model === 'per_hour' || viewingService.pricing_model === 'per_hour_per_person';
      setModalGuests(isPerperson ? (eventDetails?.guestCount || 50) : (viewingService.min_guests || 50));
      setModalHours(isPerHour ? (eventDetails?.duration || 4) : 4);
    }
  }, [viewingService, eventDetails]);

  const pricing = useMemo(() => {
    if (!viewingService) return { model: '', total: 0, label: '' };
    const { pricing_model } = viewingService;
    const isDropoff = selectedServiceType === 'dropoff' && viewingService.has_dropoff_option;

    if (isDropoff) {
      return { model: 'dropoff', total: viewingService.dropoff_price || 0, label: 'Drop-off flat rate' };
    }

    // Calculate options price modifier
    let optionsMod = 0;
    if (viewingService.option_groups) {
      for (const og of viewingService.option_groups) {
        const sel = selectedOptions[og.option_id];
        if (!sel) continue;
        const selectedIds = Array.isArray(sel) ? sel : [sel];
        for (const choiceId of selectedIds) {
          const choice = og.choices?.find(c => c.choice_id === choiceId);
          if (choice?.price_modifier) optionsMod += choice.price_modifier;
          if (choice?.flat_price) optionsMod += choice.flat_price;
        }
      }
    }

    // Customer-facing unit price (NOT wholesale)
    const unitPrice = customerUnitPrice(viewingService) + optionsMod;

    switch (pricing_model) {
      case 'per_person':
        return { model: 'per_person', total: unitPrice * modalGuests, label: `$${unitPrice.toFixed(2)} x ${modalGuests} guests`, showGuests: true };
      case 'per_hour':
        return { model: 'per_hour', total: unitPrice * modalHours, label: `$${unitPrice.toFixed(2)} x ${modalHours} hrs`, showHours: true };
      case 'per_hour_per_person':
        return { model: 'per_hour_per_person', total: unitPrice * modalGuests * modalHours, label: `$${unitPrice.toFixed(2)} x ${modalGuests} guests x ${modalHours} hrs`, showGuests: true, showHours: true };
      case 'flat_rate':
      default:
        return { model: 'flat_rate', total: unitPrice, label: 'Flat rate' };
    }
  }, [viewingService, modalGuests, modalHours, selectedOptions, selectedServiceType]);

  if (!viewingService) return null;

  const isPackageMode = !!customizingPkgServiceId;
  const minGuests = viewingService.min_guests || 10;
  const maxGuests = viewingService.max_guests || 500;

  const handleAddToCart = () => {
    if (isPackageMode) { savePackageServiceOptions(); return; }
    const isDropoff = selectedServiceType === 'dropoff' && viewingService.has_dropoff_option;
    const overrides = {};
    if (pricing.showGuests) overrides.guestCount = modalGuests;
    if (pricing.showHours) overrides.hours = modalHours;
    addToCart(viewingService, selectedOptions, isDropoff, overrides);
  };

  return (
    <Dialog open={!!viewingService} onOpenChange={() => setViewingService(null)}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-2">
          <DialogHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <DialogTitle className="text-xl sm:text-2xl">{viewingService.name}</DialogTitle>
              {viewingService.is_featured && <Badge className="bg-amber-500">Featured</Badge>}
            </div>
            <div className="flex gap-1.5 flex-wrap mt-2">
              <Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[viewingService.category]}</Badge>
              {viewingService.sub_category && <Badge variant="outline" className="text-xs">{SUB_CATEGORY_LABELS[viewingService.sub_category]}</Badge>}
            </div>
          </DialogHeader>

          {/* Gallery */}
          {viewingService.gallery_images?.length > 0 && (
            <div className="space-y-3 mt-4">
              <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                <img src={viewingService.gallery_images[selectedGalleryImage] || viewingService.image_url} alt={viewingService.name} className="w-full h-full object-cover" />
              </div>
              {viewingService.gallery_images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {viewingService.gallery_images.map((img, idx) => (
                    <button key={idx} onClick={() => setSelectedGalleryImage(idx)} className={`w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 ${selectedGalleryImage === idx ? 'border-indigo-500' : 'border-transparent'}`}>
                      <img src={img} alt={`${viewingService.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <Tabs value={detailTab} onValueChange={setDetailTab} className="mt-4">
            <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="included">What's Included</TabsTrigger>
              <TabsTrigger value="requirements">Host Requirements</TabsTrigger>
              {viewingService.option_groups?.length > 0 && (
                <TabsTrigger value="customize" className="bg-indigo-500/10 data-[state=active]:bg-indigo-500 data-[state=active]:text-foreground">Customize</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              <div>
                <h4 className="font-semibold mb-2">What to Expect</h4>
                <p className="text-muted-foreground">{viewingService.what_to_expect || viewingService.description}</p>
              </div>
              {viewingService.has_dropoff_option && (
                <div className="p-4 border rounded-lg bg-gradient-to-r from-emerald-500/10 to-blue-500/10">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><Truck className="h-5 w-5 text-emerald-500" /> Service Type</h4>
                  <RadioGroup value={selectedServiceType} onValueChange={setSelectedServiceType} className="space-y-3">
                    <label className={`flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedServiceType === 'serviced' ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:border-indigo-300'}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="serviced" />
                        <Utensils className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-500" />
                        <div><span className="font-semibold text-sm sm:text-base">Full Service</span><p className="text-xs sm:text-sm text-muted-foreground">Our staff will set up, serve, and clean up</p></div>
                      </div>
                      <div className="text-left sm:text-right ml-8 sm:ml-0">
                        <span className="text-base sm:text-lg font-bold text-indigo-500">{formatPrice(viewingService)}</span>
                        <p className="text-xs text-muted-foreground">{PRICING_MODEL_LABELS[viewingService.pricing_model]}</p>
                      </div>
                    </label>
                    <label className={`flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedServiceType === 'dropoff' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:border-emerald-300'}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="dropoff" />
                        <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-500" />
                        <div>
                          <span className="font-semibold text-sm sm:text-base">Drop-off Only</span>
                          <p className="text-xs sm:text-sm text-muted-foreground">We deliver to your venue, you handle the rest</p>
                          {viewingService.dropoff_minimum_order && <p className="text-xs text-amber-600 mt-1">Min. order: ${viewingService.dropoff_minimum_order}</p>}
                        </div>
                      </div>
                      <div className="text-left sm:text-right ml-8 sm:ml-0">
                        <span className="text-base sm:text-lg font-bold text-emerald-500">${viewingService.dropoff_price?.toFixed(2)}</span>
                        <p className="text-xs text-muted-foreground">Flat Rate</p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              )}
              {!viewingService.has_dropoff_option && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div><p className="text-sm text-muted-foreground">Base Price</p><p className="text-2xl font-bold text-indigo-500">{formatPrice(viewingService)}</p><p className="text-xs text-muted-foreground">{PRICING_MODEL_LABELS[viewingService.pricing_model]}</p></div>
                    {viewingService.avg_rating && (
                      <div className="flex items-center gap-1 text-yellow-500"><Star className="h-5 w-5 fill-yellow-500" /><span className="font-bold">{viewingService.avg_rating}</span><span className="text-xs text-muted-foreground">({viewingService.review_count} reviews)</span></div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="included" className="mt-4">
              <div className="space-y-4">
                {viewingService.whats_included?.length > 0 ? viewingService.whats_included.map((item, i) => (
                  <div key={i} className="flex items-start gap-3"><Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>{item}</span></div>
                )) : <p className="text-muted-foreground">Details available upon booking.</p>}
              </div>
            </TabsContent>

            <TabsContent value="requirements" className="mt-4">
              <div className="space-y-4">
                {viewingService.host_requirements?.length > 0 ? viewingService.host_requirements.map((req, i) => (
                  <div key={i} className="flex items-start gap-3"><span className="text-amber-500 font-bold flex-shrink-0">•</span><span>{req}</span></div>
                )) : <p className="text-muted-foreground">No special requirements.</p>}
              </div>
            </TabsContent>

            {viewingService.option_groups?.length > 0 && (
              <TabsContent value="customize" className="mt-4 space-y-6">
                {viewingService.option_groups.map(og => (
                  <div key={og.option_id} className="space-y-3">
                    <div>
                      <h4 className="font-semibold">{og.name}</h4>
                      {og.description && <p className="text-sm text-muted-foreground">{og.description}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{og.multiple_select ? 'Select multiple' : 'Select one'}{og.required && ' • Required'}</p>
                    </div>
                    <div className="space-y-2">
                      {og.choices?.map(choice => {
                        const sel = selectedOptions[og.option_id];
                        const isSelected = og.multiple_select ? (sel || []).includes(choice.choice_id) : sel === choice.choice_id;
                        return (
                          <div key={choice.choice_id} onClick={() => handleOptionChange(og.option_id, choice.choice_id, og.multiple_select)}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10' : 'border-border hover:border-indigo-200'}`}
                            data-testid={`option-${og.option_id}-${choice.choice_id}`}>
                            {og.multiple_select ? (
                              <Checkbox checked={isSelected} className="pointer-events-none" />
                            ) : (
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-indigo-500' : 'border-muted-foreground'}`}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                              </div>
                            )}
                            {choice.image_url && <img src={choice.image_url} alt={choice.name} className="w-12 h-12 rounded object-cover" />}
                            <div className="flex-1">
                              <p className="font-medium text-sm">{choice.name}</p>
                              {choice.description && <p className="text-xs text-muted-foreground">{choice.description}</p>}
                            </div>
                            <span className={`text-sm font-semibold ${choice.price_modifier > 0 ? 'text-amber-600' : choice.price_modifier < 0 ? 'text-green-600' : choice.flat_price > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {choice.price_modifier > 0 ? `+$${choice.price_modifier.toFixed(2)}` : choice.price_modifier < 0 ? `-$${Math.abs(choice.price_modifier).toFixed(2)}` : choice.flat_price > 0 ? `+$${choice.flat_price.toFixed(2)}` : 'Included'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* Sticky Pricing Footer */}
        <div className="border-t bg-background px-4 sm:px-6 py-3 flex flex-col gap-3" data-testid="service-pricing-footer">
          {/* Guest / Hour Controls */}
          {!isPackageMode && (
            <div className="flex flex-wrap items-center gap-4">
              {pricing.showGuests && (
                <div className="flex items-center gap-2" data-testid="guest-count-control">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Guests</span>
                  <div className="flex items-center border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setModalGuests(g => Math.max(minGuests, g - 10))}
                      className="px-2.5 py-1.5 hover:bg-muted transition-colors text-muted-foreground"
                      data-testid="guests-minus"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="px-3 py-1.5 text-sm font-bold min-w-[48px] text-center" data-testid="guests-value">{modalGuests}</span>
                    <button
                      onClick={() => setModalGuests(g => Math.min(maxGuests, g + 10))}
                      className="px-2.5 py-1.5 hover:bg-muted transition-colors text-muted-foreground"
                      data-testid="guests-plus"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
              {pricing.showHours && (
                <div className="flex items-center gap-2" data-testid="hours-control">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Hours</span>
                  <div className="flex items-center border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setModalHours(h => Math.max(1, h - 1))}
                      className="px-2.5 py-1.5 hover:bg-muted transition-colors text-muted-foreground"
                      data-testid="hours-minus"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="px-3 py-1.5 text-sm font-bold min-w-[40px] text-center" data-testid="hours-value">{modalHours}</span>
                    <button
                      onClick={() => setModalHours(h => Math.min(12, h + 1))}
                      className="px-2.5 py-1.5 hover:bg-muted transition-colors text-muted-foreground"
                      data-testid="hours-plus"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Total + Add to Cart */}
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground truncate" data-testid="price-breakdown">{pricing.label}</p>
              <p className="text-xl sm:text-2xl font-black text-indigo-500" data-testid="price-total">${pricing.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <Button size="lg" onClick={handleAddToCart} className="flex-shrink-0 gap-2 px-6" data-testid="add-service-to-cart">
              <ShoppingCart className="h-4 w-4" />
              {isPackageMode ? 'Save Options' : 'Add to Cart'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
